import type {
  BehaviourConfig,
  BotDetail,
  BotState,
  DesiredState,
  LogLevel,
  McAccountStatus,
  McAuthType,
  RunnerLoad,
  ServerHook
} from '@apiary/shared'

/**
 * DTO REST-эндпоинтов из docs/06. Здесь **только** те структуры, которых нет
 * в `@apiary/shared`: состояния бота, поведения, ошибки и WS-события берутся
 * из общего пакета и никогда не переопределяются локально.
 *
 * Где документ не фиксирует форму ответа (см. отчёт по неоднозначностям),
 * поле помечено комментарием «docs/06: не описано».
 */

/** docs/06 §1: cursor-based пагинация. */
export interface Paginated<T> {
  items: T[]
  nextCursor: string | null
}

// ───────────────────────────── auth ─────────────────────────────

export type OrgRole = 'OWNER' | 'ADMIN' | 'OPERATOR' | 'VIEWER'

export interface UserDto {
  id: string
  email: string
  displayName: string
  locale?: string
  totpEnabled?: boolean
}

/** Элемент `orgs[]` в ответе логина / `memberships` в `/auth/me`. */
export interface MembershipDto {
  orgId: string
  orgName: string
  role: OrgRole
}

export interface LoginRequest {
  email: string
  password: string
  totp?: string
}

export interface LoginResponse {
  accessToken: string
  expiresIn: number
  /**
   * CSRF-токен double-submit. Приходит и в cookie, и здесь: клиент держит его в
   * памяти и не зависит от читаемости cookie (см. lib/api.ts).
   */
  csrf: string
  user: UserDto
  orgs: MembershipDto[]
}

export interface RegisterRequest {
  email: string
  password: string
  displayName: string
  inviteCode?: string
}

export interface RefreshResponse {
  accessToken: string
  expiresIn: number
  csrf: string
}

export interface MeResponse {
  user: UserDto
  memberships: MembershipDto[]
  activeOrgId: string
  permissions: string[]
}

/**
 * `PATCH /auth/me` (docs/06 §2). Оба поля необязательны, но хотя бы одно
 * обязано быть — сервер иначе отвечает 400. Занятая почта приезжает как
 * `409 VALIDATION_FAILED` с `errors[].path = 'email'`, поэтому ошибка
 * показывается под полем, а не общим баннером.
 */
export interface UpdateProfileRequest {
  displayName?: string
  email?: string
}

/**
 * `POST /auth/totp/setup` (docs/06 §2).
 *
 * Полный секрет есть ТОЛЬКО внутри `otpauthUrl` — из неё рисуется QR.
 * `secretMasked` — подпись под кодом: «это точно тот секрет, что я завёл»,
 * и по ней нельзя восстановить сам секрет.
 */
export interface TotpSetupDto {
  otpauthUrl: string
  secretMasked: string
}

/** `POST /auth/totp/enable`. Коды выдаются ОДИН раз и больше нигде не доступны. */
export interface TotpEnableResponse {
  recoveryCodes: string[]
}

// ───────────────────────────── боты ─────────────────────────────

export interface BotDto {
  id: string
  name: string
  /** Последнее известное состояние (`bot.lastState`, docs/05 §2). */
  state: BotState
  desiredState: DesiredState
  serverId: string
  serverName: string
  serverAddress: string
  mcAccountId: string
  mcAccountLabel: string
  /**
   * UUID Minecraft-аккаунта для аватарки (`/api/skins/:uuid.png`).
   * `null` у offline и у неавторизованных — тогда рисуется силуэт.
   */
  mcAccountUuid: string | null
  autoStart: boolean
  behaviours: BehaviourConfig
  lastDetail: BotDetail | null
  lastStateAt: string
  /** null — бот не запущен; используется для uptime. */
  startedAt: string | null
  runnerId: string | null
  /** Причина последней остановки или ошибки — показывается в шапке бота. */
  lastError: string | null
  /** Когда воркер попробует снова после разрыва (RECONNECT_WAIT). */
  nextAttemptAt: string | null
  createdAt: string
}

/** `GET /bots/:id` — сущность + `behaviours` + `runner` (docs/06 §5). */
export interface BotDetailDto extends BotDto {
  server: ServerDto
  mcAccount: McAccountDto
  runner: RunnerDto | null
}

export interface CreateBotRequest {
  name: string
  mcAccountId: string
  serverId: string
  behaviours?: BehaviourConfig
  autoStart?: boolean
}

export interface UpdateBotRequest {
  name?: string
  behaviours?: BehaviourConfig
  autoStart?: boolean
  serverId?: string
  mcAccountId?: string
}

export interface BotEventDto {
  id: string
  botId: string
  level: LogLevel
  kind: string
  message: string
  data?: unknown
  at: string
}

export interface BotListQuery {
  state?: BotState
  serverId?: string
  q?: string
  limit?: number
  cursor?: string
}

export interface BotEventQuery {
  level?: LogLevel
  kind?: string
  q?: string
  from?: string
  to?: string
  limit?: number
  cursor?: string
}

/** Ответ команд: 202 + `commandId` (docs/06 §5). */
export interface CommandAcceptedDto {
  commandId: string
}

// ───────────────────────── MC-аккаунты ──────────────────────────

export interface McAccountDto {
  id: string
  label: string
  authType: McAuthType
  status: McAccountStatus
  gamertag: string | null
  mcUuid: string | null
  /**
   * Почта Microsoft, под которой входили. Её вводит сам пользователь: device-code
   * поток запрашивает scope `XboxLive.signin`, id_token в ответе не приходит, и
   * узнать адрес автоматически негде. Нужна, чтобы не путать аккаунты в списке.
   */
  msaEmail: string | null
  tokenExpiresAt: string | null
  lockedByBotId: string | null
}

export interface CreateMcAccountRequest {
  label: string
  authType: McAuthType
  username?: string
  msaEmail?: string
}

export interface UpdateMcAccountRequest {
  label?: string
  /** Пустая строка очищает поле. */
  msaEmail?: string
  /** Ник OFFLINE-аккаунта. Для MICROSOFT приходит из профиля и не меняется. */
  username?: string
}

/** `GET /mc-accounts/:id/auth-status` — фоллбэк для клиентов без WS. */
export interface McAuthStatusDto {
  status: McAccountStatus
  userCode?: string
  verificationUri?: string
  expiresAt?: string
  error?: string
}

// ────────────────────── профили серверов ───────────────────────

export type ServerVersionMode = 'auto' | 'manual'

export interface ServerProbeDto {
  online: boolean
  motd: string | null
  version: string | null
  protocol: number | null
  /** docs/06 §4 отдаёт `players` без уточнения формы — считаем счётчиком. */
  players: { online: number; max: number } | null
  latencyMs: number | null
  supportedByCore: boolean
  /** docs/06: не описано; нужно UI, чтобы показать «кэш пинга» из §4. */
  at: string
}

export interface ServerDto {
  id: string
  name: string
  host: string
  port: number
  versionMode: ServerVersionMode
  /** Явная версия при `versionMode = manual`. */
  version: string | null
  joinCommand: string | null
  hooks: ServerHook[]
  /** Кэш пинга из списка (docs/06 §4 «список с кэшем пинга»). */
  probe: ServerProbeDto | null
}

export interface CreateServerRequest {
  name: string
  host: string
  port: number
  versionMode: ServerVersionMode
  version?: string | null
  joinCommand?: string | null
  hooks?: ServerHook[]
}

export interface ProbeRequest {
  host: string
  port: number
}

// ───────────────────── дашборд / система ───────────────────────

export interface RunnerDto {
  id: string
  name: string
  online: boolean
  version: string
  load: RunnerLoad | null
  maxBots: number
}

/**
 * `GET /dashboard/summary`. `uptimeHours24` в docs/06 нет — но `UptimeBar`
 * (docs/10 §4) требует 24 почасовых сегмента, поэтому поле опциональное:
 * если API его не отдаёт, полоса показывает «нет данных».
 */
export interface DashboardSummaryDto {
  botsTotal: number
  botsPlaying: number
  incidents24h: number
  uptimePct24h: number
  runners: RunnerDto[]
  uptimeHours24?: UptimeHourDto[]
  /** Состав флота по состояниям; сервер отдаёт все состояния, включая нулевые. */
  stateBreakdown?: StateCountDto[]
  /** Инциденты по часам за сутки; та же ось часов, что у `uptimeHours24`. */
  incidentsHours24?: IncidentHourDto[]
}

export interface StateCountDto {
  state: BotState
  count: number
}

export interface IncidentHourDto {
  /** Час в UTC, 0..23. */
  hour: number
  count: number
  /**
   * Из чего сложился час: «отключение 2, ошибка 1». Необязательное поле —
   * сервер прошлой версии его не отдаёт, а график обязан пережить такой ответ
   * (панель обновляется отдельно от api).
   */
  kinds?: { kind: string; count: number }[]
}

export interface UptimeHourDto {
  /** Час в UTC, 0..23. */
  hour: number
  /** null — данных за этот час нет. */
  uptimePct: number | null
}

// ───────────────────────────── аудит ─────────────────────────────

/**
 * Запись аудита (`GET /audit`, docs/06 §7 + docs/09 §8).
 *
 * `actorUserName`/`actorApiKeyName` API дорезолвливает по id (см.
 * `AuditService.resolveActors`): в самой таблице `AuditLog` лежат только
 * идентификаторы, а экран обязан отвечать на вопрос «кто», а не «какой cuid».
 * `null` во всех трёх полях актора — системное действие (например, джоба
 * retention) или удалённый пользователь.
 */
export interface AuditEntryDto {
  id: string
  at: string
  /** Сырой код действия, например `auth.login.success`. */
  action: string
  actorUserId: string | null
  actorApiKeyId: string | null
  actorUserName: string | null
  actorUserEmail: string | null
  actorApiKeyName: string | null
  /** Вид объекта: `bot`, `server`, `mcAccount`, `member`, … */
  targetType: string | null
  targetId: string | null
  ip: string | null
  userAgent: string | null
  /** Произвольный JSON из хендлера, уже прогнанный через redaction на сервере. */
  meta: unknown
}

export interface AuditQuery {
  action?: string
  actorUserId?: string
  actorApiKeyId?: string
  targetType?: string
  targetId?: string
  from?: string
  to?: string
  limit?: number
  cursor?: string
}

/**
 * `GET /system/info` (docs/06 §7). Версии — `null`, когда слой совместимости не
 * собрался: тогда причина лежит в `compat.error`, и экран обязан показать её, а
 * не пустое место.
 */
export interface SystemInfoDto {
  appVersion: string
  mineflayer: string | null
  minecraftData: string | null
  prismarineChunk: string | null
  supportedVersions: string[]
  patches: string[]
  compat: {
    ok: boolean
    targetVersion: string
    targetProtocol: number | null
    error?: string
  }
}

/**
 * Лимиты организации из `Org.limits` (docs/05 §2). Имена полей — ровно
 * серверные: раньше здесь лежали выдуманные `bots/mcAccounts/viewerSessions`,
 * из-за чего экран настроек читал `undefined` и печатал «3 из » с пустым
 * местом вместо числа.
 *
 * Лимита на MC-аккаунты в модели нет вовсе — это не забытое поле, а факт,
 * который UI обязан показывать как «без ограничения».
 */
export interface OrgLimitsDto {
  maxBots: number
  maxViewers: number
  logRetentionDays: number
}

/** Фактическое потребление. Набор ключей другой, чем у лимитов, — так в API. */
export interface OrgUsageDto {
  bots: number
  mcAccounts: number
  servers: number
  members: number
  activeViewers: number
}

export interface OrgCurrentDto {
  id: string
  name: string
  slug: string
  plan: string
  limits: OrgLimitsDto
  usage: OrgUsageDto
  role: string
  createdAt: string
}

/**
 * Сессия камеры (docs/06 §6). `assetsVersion` — версия ассетов, которую грузит
 * клиент: для 26.x это 1.21.11 (ADR-11).
 */
export interface ViewerSessionDto {
  sessionId: string
  room: string
  botId: string
  assetsVersion: string
  viewDistance: number
  startedAt: string
  bytesSent: number
}

export interface ViewerSessionListDto {
  items: ViewerSessionDto[]
  /** Лимит org (`Org.limits.maxViewers`) — UI показывает «2 из 5». */
  limit: number
}

// ───────────────────────────── алерты ─────────────────────────────

/**
 * Правила алертов (`/api/alerts`, docs/06 §7.1, FR-O4).
 *
 * Списки видов и каналов продублированы здесь как `as const`, а не взяты из
 * `@apiary/shared`: их там нет — серверные `ALERT_KINDS` живут в
 * `apps/api/src/modules/alerts/alert-rule-kinds.ts`, а импортировать из другого
 * приложения фронт не может. Порядок в массиве — порядок в выпадающем списке.
 */
export const ALERT_KINDS = ['botDown', 'authFailed', 'kickMatch', 'degraded'] as const
export type AlertKind = (typeof ALERT_KINDS)[number]

export const ALERT_CHANNELS = ['telegram', 'webhook'] as const
export type AlertChannel = (typeof ALERT_CHANNELS)[number]

/**
 * Правило в ответе API.
 *
 * `target` здесь нет и быть не может: сырой адрес доставки не возвращает ни
 * один эндпоинт (docs/06 §7.1). UI видит только `targetMasked` — и поэтому
 * НИКОГДА не отправляет маску обратно в `PATCH`.
 */
export interface AlertRuleDto {
  id: string
  name: string
  kind: AlertKind
  channel: AlertChannel
  /** `•••7890`, `https://hooks.slack.com/…` или «чат по умолчанию». */
  targetMasked: string
  /** Форма зависит от `kind`: `{minutes}` у botDown, `{pattern,flags?}` у kickMatch. */
  config: Record<string, unknown>
  enabled: boolean
  lastFiredAt: string | null
  createdAt: string
}

export interface CreateAlertRuleRequest {
  name: string
  kind: AlertKind
  channel: AlertChannel
  /** Пустая строка у telegram = «чат по умолчанию из окружения». */
  target: string
  config: Record<string, unknown>
  enabled: boolean
}

/**
 * PATCH. `target: undefined` означает «не трогать» — именно так и надо, когда
 * пользователь не менял адрес: прислать прежнее значение UI не может, он видит
 * маску. Смена `channel` без нового `target` — 409 на сервере.
 */
export interface UpdateAlertRuleRequest {
  name?: string
  kind?: AlertKind
  channel?: AlertChannel
  target?: string
  config?: Record<string, unknown>
  enabled?: boolean
}

export interface AlertRuleListQuery {
  limit?: number
  cursor?: string
  kind?: AlertKind
  enabled?: boolean
}

/**
 * `POST /alerts/:id/test`. Приходит с кодом 200 даже при неудаче доставки:
 * «webhook вернул 404» — это результат проверки, а не сбой запроса, и место
 * ему в форме рядом с кнопкой, а не в тосте.
 */
export interface AlertTestResultDto {
  delivered: boolean
  error: string | null
}
